import { useState } from 'react'
import { AlertTriangle, ShieldAlert } from 'lucide-react'
import {
  CurrencyValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useApplications, useOfferMutations, useOffers } from '../api'
import { OFFER_STATUS_LABELS } from '../types'
import type { OfferStatus } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

const STATUS_TONE: Record<OfferStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'info',
  SENT: 'warning',
  ACCEPTED: 'success',
  DECLINED: 'danger',
  WITHDRAWN: 'neutral',
  EXPIRED: 'neutral',
}

/**
 * Offers.
 *
 * The banner is not decoration: opening this page writes a row to the sensitive access log
 * for every offer listed, and people should know that reading somebody's unaccepted salary
 * is recorded.
 */
export function OffersPage() {
  useBreadcrumb()
  const offers = useOffers()
  const applications = useApplications()
  const { prepare, submit, approve, send, respond } = useOfferMutations()

  const [draft, setDraft] = useState<{
    applicationId: string; basicSalary: string; totalPackage: string; currency: string
    joiningDate: string; probationMonths: string; validUntil: string
  } | null>(null)
  const [declining, setDeclining] = useState<{ id: string; reason: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const offerable = applications.data?.filter(
    (application) => application.status === 'IN_PROGRESS' || application.status === 'OFFERED') ?? []

  const act = (work: () => Promise<unknown>, fallback: string) => {
    void (async () => {
      setError(null)
      try {
        await work()
      } catch (caught) {
        setError(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Offers"
        subtitle="What has been offered, to whom, and whether they have answered."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                applicationId: '', basicSalary: '', totalPackage: '', currency: 'AED',
                joiningDate: '', probationMonths: '', validUntil: '',
              })
            }}
          >
            Prepare an offer
          </Button>
        }
      />

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <ShieldAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Opening this page is recorded. An offer names somebody's salary before they have
        accepted it, and usually before their current employer knows they are leaving, so
        every read is written to the sensitive access log — the same as a payslip.
      </p>

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <Card>
        {offers.isPending && <Skeleton className="m-4 h-24" />}
        {offers.isError && <ErrorState error={offers.error} onRetry={() => void offers.refetch()} />}
        {offers.isSuccess && offers.data.length === 0 && (
          <EmptyState
            title="Nothing has been offered"
            description="An offer is prepared against an application, approved by somebody other than whoever wrote it, sent, and then answered."
          />
        )}
        {offers.isSuccess && offers.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Reference</th>
                  <th className="p-3 text-start font-medium">Candidate</th>
                  <th className="p-3 text-end font-medium">Basic</th>
                  <th className="p-3 text-end font-medium">Package</th>
                  <th className="p-3 text-start font-medium">Against the range</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {offers.data.map((offer) => (
                  <tr key={offer.id} className="border-b border-slate-100 align-top">
                    <td className="p-3 font-mono">{offer.reference}</td>
                    <td className="p-3">{offer.candidateName ?? '—'}</td>
                    <td className="p-3 text-end">
                      <CurrencyValue value={offer.basicSalary} currency={offer.currency} />
                    </td>
                    <td className="p-3 text-end">
                      <CurrencyValue value={offer.totalPackage} currency={offer.currency} />
                    </td>
                    <td className="p-3">
                      {offer.withinSalaryRange === false ? (
                        <span className="inline-flex items-start gap-1 text-amber-700">
                          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                          <span className="max-w-xs text-xs">{offer.rangeNote}</span>
                        </span>
                      ) : (
                        <span className="max-w-xs text-xs text-slate-600">
                          {offer.rangeNote}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={offer.status}
                        tone={STATUS_TONE[offer.status as OfferStatus] ?? 'neutral'}
                        label={OFFER_STATUS_LABELS[offer.status as OfferStatus] ?? offer.status}
                      />
                    </td>
                    <td className="p-3 text-end">
                      <div className="flex justify-end gap-2">
                        {offer.status === 'DRAFT' && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => act(
                              () => submit.mutateAsync(offer.id),
                              'That offer could not be sent for approval')}
                          >
                            Send for approval
                          </Button>
                        )}
                        {offer.status === 'PENDING_APPROVAL' && (
                          <Button
                            type="button"
                            onClick={() => act(
                              () => approve.mutateAsync(offer.id),
                              'That offer could not be approved')}
                          >
                            Approve
                          </Button>
                        )}
                        {offer.status === 'APPROVED' && (
                          <Button
                            type="button"
                            onClick={() => act(
                              () => send.mutateAsync({ id: offer.id }),
                              'That offer could not be marked as sent')}
                          >
                            Mark sent
                          </Button>
                        )}
                        {offer.status === 'SENT' && (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setError(null)
                                setDeclining({ id: offer.id, reason: '' })
                              }}
                            >
                              Declined
                            </Button>
                            <Button
                              type="button"
                              onClick={() => act(
                                () => respond.mutateAsync({ id: offer.id, accepted: true }),
                                'That answer could not be recorded')}
                            >
                              Accepted
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        size="lg"
        title="Prepare an offer"
        description="It is checked against the requisition's salary range — a warning for the approver, not a refusal."
        submitLabel="Save draft"
        loading={prepare.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await prepare.mutateAsync({
                applicationId: draft.applicationId,
                body: {
                  basicSalary: draft.basicSalary ? Number(draft.basicSalary) : null,
                  totalPackage: draft.totalPackage ? Number(draft.totalPackage) : null,
                  currency: draft.currency,
                  joiningDate: draft.joiningDate || null,
                  probationMonths: draft.probationMonths
                    ? Number(draft.probationMonths) : null,
                  validUntil: draft.validUntil || null,
                },
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That offer could not be prepared')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="offer-application">Application</Label>
              <Select
                id="offer-application"
                className="w-full"
                value={draft.applicationId}
                onChange={(event) => setDraft({ ...draft, applicationId: event.target.value })}
              >
                <option value="">Choose an application…</option>
                {offerable.map((application) => (
                  <option key={application.id} value={application.id}>
                    {application.candidateName} — {application.requisitionTitle}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="offer-basic">Basic salary</Label>
              <Input
                id="offer-basic" type="number" value={draft.basicSalary}
                onChange={(event) => setDraft({ ...draft, basicSalary: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="offer-package">Total package</Label>
              <Input
                id="offer-package" type="number" value={draft.totalPackage}
                onChange={(event) => setDraft({ ...draft, totalPackage: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="offer-currency">Currency</Label>
              <Input
                id="offer-currency" maxLength={3} value={draft.currency}
                onChange={(event) =>
                  setDraft({ ...draft, currency: event.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <Label htmlFor="offer-joining">Joining date</Label>
              <DatePicker
                id="offer-joining" value={draft.joiningDate}
                onChange={(event) => setDraft({ ...draft, joiningDate: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="offer-probation">Probation, months</Label>
              <Input
                id="offer-probation" type="number" value={draft.probationMonths}
                onChange={(event) =>
                  setDraft({ ...draft, probationMonths: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="offer-valid">Valid until</Label>
              <DatePicker
                id="offer-valid" value={draft.validUntil}
                onChange={(event) => setDraft({ ...draft, validUntil: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                No default: how long an offer stands is a commercial choice.
              </p>
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={declining != null}
        onOpenChange={(open) => { if (!open) setDeclining(null) }}
        title="Record a declined offer"
        description="Why it was declined is the most useful thing recruitment data has to say."
        submitLabel="Record"
        loading={respond.isPending}
        error={error}
        onSubmit={() => {
          if (!declining) return
          void (async () => {
            setError(null)
            try {
              await respond.mutateAsync({
                id: declining.id, accepted: false, declineReason: declining.reason,
              })
              setDeclining(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That answer could not be recorded')
            }
          })()
        }}
      >
        {declining && (
          <div>
            <Label htmlFor="decline-reason">Why did they decline?</Label>
            <Input
              id="decline-reason" value={declining.reason}
              onChange={(event) => setDeclining({ ...declining, reason: event.target.value })}
            />
            <p className="mt-1 text-xs text-slate-600">
              "Declined" on its own tells nobody whether to change the pay, the process or the
              role.
            </p>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
